import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {ConfirmationService, SelectItem, TreeNode} from 'primeng/api';
import {Field, Filter, GroupField, InfoView, Query} from '../filter.type';
import {TreeTable} from 'primeng/treetable';
import {ClassicTreeSave} from '../filter-preset/filter-preset.component';
import {FilterPresetService} from '../filter-preset/filter-preset.service';
import {TableMetaComponent} from '../../table-data/table-meta/table-meta.component';
import {FilterBuilderService} from '../../../services/filter-builder.service';
import {HavingCountTable, SaveQuery, Table} from '@tj/tj-dmp-lib-api';

@Component({
    selector: 'dmp-filter-tree-table',
    templateUrl: './filter-tree-table.component.html',
    styleUrls: ['./filter-tree-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class FilterTreeTableComponent implements OnInit {
    @Input() public filters: TreeNode[] = [];
    @Output() public filtersChange = new EventEmitter<TreeNode[]>();
    public groups: SelectItem[] = [];
    @Input() fields!: Field[] | GroupField[];

    @ViewChild('tt', {static: true}) tt: TreeTable;
    @Input() view!: InfoView;
    @Input() query!: Query;
    @Input() tenant!: string;
    @Input() saveQuery: ClassicTreeSave = null;
    @Output() public saveQueryChange  = new EventEmitter<ClassicTreeSave|TreeNode>();
    @Output() public saveQueryUpdate  = new EventEmitter<ClassicTreeSave|TreeNode>();
    @Input() readOnly = false;
    @Input() table!: TableMetaComponent;
    @Input() displayQuerySelect = true;

    @Output() onInit = new EventEmitter<boolean>();
    @Input() tables!: Table[];
    public having: HavingCountTable[] = [];

    constructor(private cdr: ChangeDetectorRef,
                private zone: NgZone,
                private confirmationService: ConfirmationService,
                private filterService: FilterPresetService,
                private filterBuilderService: FilterBuilderService) {
    }

    ngOnInit(): void {
        this.groups = [{
            label: 'ET',
            value: 'AND'
        }, {
            label: 'OU',
            value: 'OR'
        }];
        this.onInit.emit(true);
    }

    public addFilter(): void {
        const filter: Filter = {
            value: null,
            filterType: null,
            field: null,
            edit: true,
            options: {}
        };
        this.filters = [...this.filters, {
            data: {
                typeGroup: 'AND'
            },
            expanded: true,
            children: [{data: filter}]
        }];
        this.filtersChange.emit(this.filters);
    }

    doAddFilter(node: any) {
        const targetNode = this.findNode(this.filters, node.node);
        targetNode.children.push({data: {
                value: null,
                filterType: null,
                field: null,
                edit: true,
                options: {}
            } as Filter});
        this.filters = [...this.filters];
        this.filtersChange.emit(this.filters);
    }

    doAddGroup(node: any) {
        const targetNode = this.findNode(this.filters, node.node);
        targetNode.children.push({
            data: {typeGroup: 'OR'},
            expanded: true,
            children: [
                {
                    data: {
                        value: null,
                        filterType: null,
                        field: null,
                        edit: true
                    } as Filter}
            ]});
        this.filters = [...this.filters];
        this.filtersChange.emit(this.filters);
    }


    private findNode(nodes: TreeNode[], nodeToFind): TreeNode {
        let nodeFind = null;
        nodes.find(node => {
            if (node === nodeToFind) {
                nodeFind = node;
            } else if (node.children) {
                nodeFind = this.findNode(node.children, nodeToFind);
            }
        });
        return nodeFind;
    }

    doRemoveFilter(node: any) {
        this.confirmationService.confirm({
            message: 'Voulez-vous le supprimer ?',
            accept: () => {
                this.filters = this.removeNode(this.filters, node.node);
                if (this.filters?.length === 1){
                    this.saveQuery.value = this.filters[0];
                } else if (this.filters?.length === 0) {
                    this.saveQuery.value = null;
                }
                this.doChangeFilter();
            }
        });
    }

    removeNode(nodes: TreeNode[], nodeToRemove: any): TreeNode[] {
        return nodes.filter(node => {
            if (node === nodeToRemove) {
                return false;
            } else if (node.children) {
                node.children = this.removeNode(node.children, nodeToRemove);
            }
            return true;
        });
    }


    private updateKey(key = '', filters = this.filters) {
        const count = 0;
        filters.forEach(filter => {
            filter.key = key.length == 0 ? '' + count : key + '-' + count;
            if (filter.children) {
                this.updateKey(filter.key, filter.children);
            }
        });
    }

    /**
     * Vide les valeurs des filtres
     * @private
     */
    public cleanValueFilters() {
        this.confirmationService.confirm({
            message: 'Voulez-vous initialiser les valeurs des filtres de recherche ?',
            accept: () => {
                this.filterService.cleanValueFilter(this.filters);
                // DEPRECATED this.filters = this.filterService.removeFilterWithNoType(this.filters);
                this.filterService.editFalseAllFilter(this.filters);
                this.filters = [...this.filters];
                this.doChangeFilter();
            }
        });

    }

    doChangeFilter($event?: Filter) {
        if (this.filters?.length === 1){
            this.saveQuery = {
                cols: [], id: '', name: '', orderings: [],
                value: this.filters[0],
                having: [],
                partner: '',
                affectedPartners: [],
                isModel: null
            };
            
        }
        this.saveQueryUpdate.emit(this.saveQuery);
    }

    public removeFilter(): void {
        if (this.filters.length !== 0) {
            this.filters = [];
            this.saveQuery = {
                cols: [],
                id: '',
                name: '',
                orderings: [],
                value: null,
                partner: '',
                affectedPartners: [],
                having: [],
                isModel: null
            };
            this.saveQueryUpdate.emit(this.saveQuery);
        }
    }

    public updateSaveQuery(saveQuery: SaveQuery) {
        const item: ClassicTreeSave = this.filterBuilderService.buildClassicTreeSaveBySaveQuery(saveQuery);
        this.filters = [item.value];
        this.saveQuery = item;
        this.having = saveQuery.having || [];
        this.saveQueryUpdate.emit(this.saveQuery);
    }

    public updateFilter($event: HavingCountTable[]) {
        this.having = $event;
        this.saveQuery.having = $event;
        this.saveQueryUpdate.emit(this.saveQuery);
    }

    public setFilter(newFilters: TreeNode[]): void {
        this.filters = newFilters;
        if (this.filters?.length === 1) {
            this.saveQuery = {
                cols: [],
                id: '',
                name: '',
                orderings: [],
                value: this.filters[0],
                partner: '',
                affectedPartners: [],
                having: [],
                isModel: null
            };
        } else {
            this.saveQuery = {
                cols: [],
                id: '',
                name: '',
                orderings: [],
                value: null,
                partner: '',
                affectedPartners: [],
                having: [],
                isModel: null
            };
        }
        this.saveQueryChange.emit(this.saveQuery);
        this.cdr.detectChanges();
    }
}

